/**
 * Ticket System Type Definitions
 * Corresponds to backend DTOs and enums for the ticket management system
 */

// Enums — kept in lock-step with backend AdventureCore.Domain.Entities.TicketType.
export enum TicketType {
  // Content moderation (0–4)
  ContentInappropriate = 0,
  ContentSpam = 1,
  ContentDuplicate = 2,
  ContentVandalism = 3,
  ContentCopyright = 4,
  // Location-specific (10–13)
  LocationPrivateProperty = 10,
  LocationMissing = 11,
  LocationInaccessible = 12,
  LocationSafety = 13,
  // User support (20–24)
  Bug = 20,
  FeatureRequest = 21,
  Question = 22,
  AccountIssue = 23,
  TechnicalSupport = 24,
  // General
  Other = 99,
}

export enum TicketPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3,
}

export enum TicketStatus {
  Pending = 0,
  Claimed = 1,
  InProgress = 2,
  AwaitingUser = 3,
  Resolved = 4,
  Closed = 5,
  Escalated = 6,
}

export enum TargetType {
  Cache = 0,
  Adventure = 1,
  POI = 2,
  Location = 3,
  Group = 4,
  User = 5,
  Discussion = 6,
  Post = 7,
}

// DTOs
export interface TicketSummaryDto {
  id: string
  type: TicketType
  priority: TicketPriority
  status: TicketStatus
  title: string
  createdAt: string
  lastUpdatedAt: string
  createdByUserId: string
  createdByDisplayName: string | null
  claimedByUserId: string | null
  claimedByDisplayName: string | null
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  isEscalated: boolean
  commentCount: number
}

export interface TicketDetailsDto {
  id: string
  type: TicketType
  priority: TicketPriority
  status: TicketStatus
  title: string
  description: string
  targetType: TargetType | null
  targetId: string | null
  createdByUserId: string
  createdByDisplayName: string | null
  createdAt: string
  claimedByUserId: string | null
  claimedByDisplayName: string | null
  claimedAt: string | null
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  assignedAt: string | null
  resolvedByUserId: string | null
  resolvedByDisplayName: string | null
  resolvedAt: string | null
  resolutionNotes: string | null
  closedByUserId: string | null
  closedByDisplayName: string | null
  closedAt: string | null
  lastUpdatedAt: string
  lastUpdatedByUserId: string | null
  lastUpdatedByDisplayName: string | null
  tags: string[] | null
  isEscalated: boolean
  escalatedAt: string | null
  escalationReason: string | null
  parentTicketId: string | null
  comments: TicketCommentDto[]
  statusHistory: TicketStatusHistoryDto[]
}

export interface TicketCommentDto {
  id: string
  ticketId: string
  authorUserId: string
  authorDisplayName: string | null
  content: string
  createdAt: string
  updatedAt: string | null
  isInternal: boolean
  attachments: string[] | null
}

export interface TicketStatusHistoryDto {
  id: string
  oldStatus: TicketStatus
  newStatus: TicketStatus
  changedByUserId: string
  changedByDisplayName: string | null
  changedAt: string
  notes: string | null
}

export interface TicketStatisticsDto {
  ticketsByStatus: Record<TicketStatus, number>
  ticketsByPriority: Record<TicketPriority, number>
  ticketsByType: Record<TicketType, number>
  totalTickets: number
  openTickets: number
  closedTickets: number
  escalatedTickets: number
  averageResolutionTimeMinutes: number
}

// Request DTOs
export interface CreateTicketRequest {
  type: TicketType
  priority?: TicketPriority
  title: string
  description: string
  targetType?: TargetType
  targetId?: string
  tags?: string[]
}

export interface AddCommentRequest {
  content: string
  isInternal?: boolean
  attachments?: string[]
}

export interface ResolveTicketRequest {
  resolutionNotes: string
}

export interface UpdatePriorityRequest {
  priority: TicketPriority
}

export interface EscalateTicketRequest {
  reason: string
}

export interface AssignTicketRequest {
  assigneeUserId: string
}

// Pagination
export interface PaginatedResponse<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

// Utility type helpers
export const TicketTypeLabels: Record<TicketType, string> = {
  [TicketType.ContentInappropriate]: 'Inappropriate Content',
  [TicketType.ContentSpam]: 'Spam',
  [TicketType.ContentDuplicate]: 'Duplicate',
  [TicketType.ContentVandalism]: 'Vandalism',
  [TicketType.ContentCopyright]: 'Copyright Violation',
  [TicketType.LocationPrivateProperty]: 'Private Property',
  [TicketType.LocationMissing]: 'Location Missing',
  [TicketType.LocationInaccessible]: 'Location Inaccessible',
  [TicketType.LocationSafety]: 'Safety Concern',
  [TicketType.Bug]: 'Bug Report',
  [TicketType.FeatureRequest]: 'Feature Request',
  [TicketType.Question]: 'Question / Support',
  [TicketType.AccountIssue]: 'Account Issue',
  [TicketType.TechnicalSupport]: 'Technical Support',
  [TicketType.Other]: 'Other',
}

export const TicketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.Low]: 'Low',
  [TicketPriority.Medium]: 'Medium',
  [TicketPriority.High]: 'High',
  [TicketPriority.Critical]: 'Critical',
}

export const TicketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Pending]: 'Pending',
  [TicketStatus.Claimed]: 'Claimed',
  [TicketStatus.InProgress]: 'In Progress',
  [TicketStatus.AwaitingUser]: 'Awaiting User',
  [TicketStatus.Resolved]: 'Resolved',
  [TicketStatus.Closed]: 'Closed',
  [TicketStatus.Escalated]: 'Escalated',
}

export const TargetTypeLabels: Record<TargetType, string> = {
  [TargetType.Cache]: 'Geocache',
  [TargetType.Adventure]: 'Adventure',
  [TargetType.POI]: 'Point of Interest',
  [TargetType.Location]: 'Location',
  [TargetType.Group]: 'Group',
  [TargetType.User]: 'User',
  [TargetType.Discussion]: 'Discussion',
  [TargetType.Post]: 'Post',
}
